import {AfterViewInit, Component, OnInit, ViewChild} from '@angular/core';
import {
  AppConfigService,
  BONDING_CONTRACT_OFFER_DETAILS,
  BondingContractOfferDto,
  CompanyRatingService,
  CompanyRatingVersionDto,
  ContractVersionService,
  GrowlService,
  LoggedUserService,
  RouterService,
  StateTransitionDto,
  TemplateService,
  TemplateSimpleDto,
  UserDto,
  UserGroupBaseDto,
  UserIdDto,
  UserService,
  VoteDto,
  VotingProcessDto,
} from '../../../bonding_shared';
import {
  BondingContractOfferStatus,
  BondingElementaryRight,
  BusinessObjectType,
  ContactNoteElementaryRight,
  ContractType,
  Currency,
  DictionaryProfile,
  DocumentType,
  Source,
  UserGroup,
} from '../../../bonding_shared/model/dictionary-ids';
import {ActivatedRoute} from '@angular/router';
import {DetailsView} from '../../../bonding_shared/components/details-view/details-view';
import {BusinessUtils} from '../../../bonding_shared/utils/business-utils';
import {BondingContractOfferService} from '../../../bonding_shared/services/bonding-contract-offer.service';
import {catchError, map, Observable, of, tap} from 'rxjs';
import {NgForm} from '@angular/forms';
import {Button} from '../../../bonding_shared/components/details-view/button';
import {BondingContractOfferVotingDialogComponent} from './components/bonding-contract-offer-voting-dialog.component';
import {DocumentListComponent} from '../../document';
import {GroupedItem} from '../../../bonding_shared/components/item-dropdown/item-grouped-dropdown.component';
import {BondingContractOfferAbstractService} from '../../../bonding_shared/services/bonding-contract-offer-abstract.service';

@Component({
  selector: BONDING_CONTRACT_OFFER_DETAILS,
  templateUrl: './bonding-contract-offer-details.component.pug',
})
export class BondingContractOfferDetailsComponent extends DetailsView implements OnInit, AfterViewInit {
  @ViewChild('ngForm', {static: true}) public ngForm: NgForm;
  @ViewChild('closeReasonForm', {static: false}) closeReasonForm: NgForm;
  @ViewChild('documentLists', {static: true}) documentListComponent: DocumentListComponent;
  @ViewChild('votingDialog')
  set votingDialog(cmp: BondingContractOfferVotingDialogComponent) {
    if (cmp) {
      this._votingDialog = cmp;
    }
  }

  get votingDialog(): BondingContractOfferVotingDialogComponent {
    return this._votingDialog;
  }

  _votingDialog: BondingContractOfferVotingDialogComponent;

  offer: BondingContractOfferDto;
  readonly = false;
  showDetails = false;
  viewParams: BondingContractOfferViewParams;
  generateUWAssessmentFormats: TemplateSimpleDto[];

  readonly ContractType = ContractType;
  readonly BondingElementaryRight = BondingElementaryRight;
  readonly ContactNoteElementaryRight = ContactNoteElementaryRight;
  readonly BusinessObjectType = BusinessObjectType;
  readonly Currency = Currency;
  readonly BondingContractOfferStatus = BondingContractOfferStatus;
  readonly Source = Source;
  readonly allTasks = [
    {id: 1, name: 'common.showTask'},
    {id: 2, name: 'common.newTask'},
    {id: 3, name: 'common.prepareProposalTask'},
  ];
  tasks = [...this.allTasks];
  newOfferButton = new Button('bondingContractOffer.details.button.newOffer', this.newOffer.bind(this), true);
  voteButton = new Button('bondingContractOffer.details.voting.vote', this.openVotingDialog.bind(this), false);
  newContractButton = new Button(
    'bondingContractOffer.details.button.newContract',
    this.onCreateFromOffer.bind(this),
    true
  );

  readonly newContractActions = [
    {id: 1, name: 'bondingContractOffer.details.button.newContract'},
    {id: 2, name: 'bondingContractOffer.details.button.markCreatedExternally'},
  ];

  votingGroups: UserGroupBaseDto[] = null;
  showRejectionReasonErrors = false;
  companyRating: CompanyRatingVersionDto;

  level2Assigner: UserIdDto[] = [];
  level3Assigner: UserIdDto[] = [];
  levelSuretyTeamLeader: UserIdDto[] = [];
  showLevel2Assigner = false;
  showLevel3Assigner = false;
  showSuretyTeamLeaders = false;
  votingSimulationFailed = false; // Flag for any operation failed while start voting process
  selectedLevel2Assigner: UserIdDto | null = null;
  selectedLevel3Assigner: UserIdDto | null = null;
  selectedSuretyTeamLeaders: UserIdDto | null = null;

  constructor(
    protected offerService: BondingContractOfferAbstractService,
    private contractService: ContractVersionService,
    private loggedUserService: LoggedUserService,
    public appService: AppConfigService,
    private route: ActivatedRoute,
    public router: RouterService,
    protected growlService: GrowlService,
    private userService: UserService,
    private templateService: TemplateService,
    private companyRatingService: CompanyRatingService
  ) {
    super(growlService);
    this.saveButton.hidden = false;
    this.cancelButton.hidden = false;
  }

  templates: TemplateSimpleDto[] = [];
  templateUW: TemplateSimpleDto;

  ngOnInit() {
    this.serverErrors = undefined;
    this.commonInit<BondingContractOfferViewParams>(this.route);
    this.loadUWTemplate();
  }

  initViewWithId(id: number) {
    this.initOffer(this.offerService.getById<BondingContractOfferDto>(id));
  }

  initViewWithCustomParams(q: BondingContractOfferViewParams) {
    if (this.offerService instanceof BondingContractOfferService) {
      if (q.inquiryVersionId) {
        this.initOffer(this.offerService.initialOffer(+q.inquiryVersionId, null, null));
      } else if (q.edgeOfferId) {
        this.initOffer(this.offerService.initialOffer(null, +q.edgeOfferId, null));
      } else if (q.copyOfferId) {
        this.initOffer(this.offerService.initialOffer(null, null, +q.copyOfferId));
      }
    }
  }

  public ngAfterViewInit(): void {
    this.form = this.ngForm.form;
  }

  initOffer(offerObs: Observable<BondingContractOfferDto>) {
    offerObs.subscribe({
      next: (pll) => this.setOffer(pll),
      error: (error) => {
        this.objectNotFound = true;
        this.handleServerError(error);
      },
    });
  }

  setOffer(inV: BondingContractOfferDto) {
    console.log('set offer: ', inV);
    this.offer = inV;
    this.handleButtons();
    this.tasks =
      this.offer?.businessStatus?.id === BondingContractOfferStatus.ACCEPTED
        ? [...this.allTasks]
        : this.allTasks.filter((t) => t.id !== 3);
  }

  onSave() {
    if (!this.formValidates()) {
      return;
    }

    /* START VOTING -> persist selected assigners on the voting object */
    if (this.isAutomaticGroupSelection && this.offer.voting) {
      this.offer.voting.level2Assigners = this.selectedLevel2Assigner ? [this.selectedLevel2Assigner] : [];
      this.offer.voting.level3Assigners = this.selectedLevel3Assigner ? [this.selectedLevel3Assigner] : [];
      this.offer.voting.suretyTeamLeaders = this.selectedSuretyTeamLeaders ? [this.selectedSuretyTeamLeaders] : [];
    }
    this.inProgress = true;
    const originalId = this.offer.id;
    this.offerService.save(this.offer).subscribe({
      next: (bcv) => {
        this.setOffer(bcv);
        this.afterObjectSaved(originalId, () => this.router.toBondingContractOfferDetails(bcv.id));
      },
      error: (error) => this.handleServerError(error, true, false),
    });
  }

  handleButtons() {
    this.saveButton.disabled = false;
    this.saveButton.hidden = this.readOnly;
    this.cancelButton.hidden = this.readOnly;
    this.newOfferButton.hidden =
      !this.offer ||
      !this.offer.id ||
      !this.offer.edge ||
      this.offer.businessStatus.id !== BondingContractOfferStatus.ACCEPTED;
    this.newContractButton.hidden = this.isNewContractHidden();
  }

  isNewContractHidden(): boolean {
    return (
      !this.offer ||
      !this.offer.id ||
      this.offer.edge ||
      !!this.offer.contractVersion ||
      this.offer.businessStatus.id !== BondingContractOfferStatus.ACCEPTED
    );
  }

  get readOnly(): boolean {
    return (
      this.offer &&
      this.offer.businessStatus &&
      [
        BondingContractOfferStatus.CANCELLED,
        BondingContractOfferStatus.EXPIRED,
        BondingContractOfferStatus.REJECTED,
        BondingContractOfferStatus.ACCEPTED,
        BondingContractOfferStatus.VOTING,
        BondingContractOfferStatus.VOTING_FINISHED,
        BondingContractOfferStatus.CONTRACT_CREATED,
        BondingContractOfferStatus.CONTRACT_CREATED_EXTERNALLY,
      ].includes(this.offer.businessStatus.id)
    );
  }

  get client() {
    return BusinessUtils.getThirdPartyCompany(this.offer && this.offer.inquiryVersion.inquiry.client);
  }

  get edgeOffer(): boolean {
    return this.offer ? this.offer.edge : this.viewParams ? !!this.viewParams.inquiryVersionId : false;
  }

  newOffer() {
    this.onNewOffer(1);
  }

  private onNewOffer(id: 1 | 2) {
    switch (id) {
      case 1:
        this.router.toBondingContractOfferDetails(null, <BondingContractOfferViewParams>{edgeOfferId: this.offer.id});
        break;
      case 2:
        this.router.toBondingContractOfferDetails(null, <BondingContractOfferViewParams>{copyOfferId: this.offer.id});
        break;
      default:
        console.log('unknown action');
    }
  }

  onCreateFromOffer() {
    this.onNewContract(1);
  }

  onNewContract(id: number) {
    switch (id) {
      case 1:
        this.contractService.createFromOffer(this.offer.id).subscribe({
          next: (cv) => this.router.toContractDetails(cv.id),
          error: (error) => this.handleServerError(error),
        });
        break;
      case 2:
        if (this.offerService instanceof BondingContractOfferService) {
          this.offerService.markCreatedExternally(this.offer.id).subscribe({
            next: (bcv) => {
              this.setOffer(bcv);
              this.afterObjectSaved();
            },
            error: (error) => this.handleServerError(error),
          });
        }
        break;
      default:
        console.log('unknown action');
    }
  }

  openVotingDialog() {
    this.votingDialog.openDialog();
  }

  onCancel(): void {
    this.clearErrors();
    this.commonInit<BondingContractOfferViewParams>(this.route);
  }

  doVote(vote: VoteDto) {
    if (this.offerService instanceof BondingContractOfferService) {
      this.offerService.doVote(this.offer.id, vote).subscribe({
        next: (bcv) => {
          this.setOffer(bcv);
          this.afterObjectSaved();
        },
        error: (error) => this.handleServerError(error),
      });
    }
  }

  get showLifecycleButton() {
    return this.offer?.businessStatus?.id !== BondingContractOfferStatus.CANCELLED;
  }

  get showStateTransitionConfirmation(): (transition: StateTransitionDto) => boolean {
    return (transition: StateTransitionDto) => {
      this.initVotingGroups(transition);
      // Dialog open for VOTING = voting object + load assigners
      if (transition.newStatus.id === BondingContractOfferStatus.VOTING) {
        if (!this.offer.voting) {
          this.offer.voting = {} as VotingProcessDto;
        }
        this.loadAssigners();
      }
      return (
        transition.newStatus.id === BondingContractOfferStatus.REJECTED ||
        transition.newStatus.id === BondingContractOfferStatus.CANCELLED ||
        transition.newStatus.id === BondingContractOfferStatus.VOTING
      );
    };
  }

  private initVotingGroups(transition: StateTransitionDto) {
    if (transition.newStatus.id === BondingContractOfferStatus.VOTING && !this.votingGroups) {
      this.userService
        .getUserGroups()
        .subscribe(
          (groups) => (this.votingGroups = groups.filter((g) => [UserGroup.EXCOM, UserGroup.SURCOM].includes(g.id)))
        );
    }
  }

  get votingProcessTypeSelection(): number | undefined {
    return DictionaryProfile.VOTING_PROCESS_TYPE_SELECTION;
  }

  get portal(): boolean {
    return this.loggedUserService.portal;
  }

  copyOffer() {
    this.onNewOffer(2);
  }

  private loadUWTemplate() {
    this.templateService
      .findByType(DocumentType.UNDERWRITING_ASSESSMENT, BusinessObjectType.BONDING_CONTRACT_OFFER, null)
      // tslint:disable-next-line:no-shadowed-variable
      .subscribe((result) => {
        this.generateUWAssessmentFormats = result;
      });
  }

  onPrintSelected(template: TemplateSimpleDto) {
    this.router.toDocumentDetailsNew(template.id, this.offer.id);
  }

  public newDocument(template: TemplateSimpleDto): void {
    this.router.toDocumentDetailsNew(template.id, this.offer.id);
  }

  confirmationValidInput(): (transition: StateTransitionDto) => () => boolean {
    return (t) => {
      if (
        t &&
        (t.newStatus.id === BondingContractOfferStatus.REJECTED ||
          t.newStatus.id === BondingContractOfferStatus.CANCELLED)
      ) {
        return () => this.validRejectionInput();
      }
      if (t && t.newStatus.id === BondingContractOfferStatus.VOTING) {
        return () => this.validVotingInput();
      }
      return () => true;
    };
  }

  validRejectionInput(): boolean {
    if (!this.closeReasonForm) {
      return true;
    }
    this.showRejectionReasonErrors = !this.closeReasonForm.valid;
    return this.closeReasonForm.valid;
  }

  newDocumentDisabled() {
    return (
      this.inProgress ||
      !this.offer ||
      !this.offer?.id ||
      !(
        this.offer?.status?.id === BondingContractOfferStatus.NEW ||
        this.offer?.status?.id === BondingContractOfferStatus.NBI_OFFER ||
        this.offer?.status?.id === BondingContractOfferStatus.VOTING
      )
    );
  }

  createPrepareSuretyProposalTask() {
    this.offerService.createPrepareSuretyProposalTask(this.offer.id).subscribe((taskId) => {
      this.router.toTaskDetails(taskId);
    });
  }

  isAvailablePandaBondingContract() {
    return true;
  }

  generateDraftPdf(template: GroupedItem) {
    const oid = this.offer.id;
    this.offerService.getBondingContractDraftDocument(
      oid,
      template.id,
      template.params.find((item) => item.key === 'name').value + '_' + oid + '.pdf'
    );
  }

  private generateDraftPdfAndSendPanda(templateId: number) {
    this.serverErrors = null;
    this.showErrors = false;
    this.inProgress = true;

    this.offerService.uploadDraftBondingContract(templateId, this.offer.id).subscribe({
      next: () => this.growlService.notice('Bonding contract draft has been sent!'),
      error: (error) => {
        this.showErrors = true;
        this.serverErrors = error;
        this.inProgress = false;
      },
      complete: () => (this.inProgress = false),
    });
  }

  get hiddenBondTypes() {
    const hiddenBondTypes = new Set();

    if (!this.offer.inquiryVersion) {
      return undefined;
    }
    if (
      (this.offer.inquiryVersion.exporterCriteriaMet && this.offer.inquiryVersion.mitigatingClimateChange) ||
      (!this.offer.inquiryVersion.exporterCriteriaMet && !this.offer.inquiryVersion.mitigatingClimateChange)
    ) {
      return undefined;
    }
    return hiddenBondTypes;
  }

  showOrCreateTask(id: 1 | 2 | 3) {
    const offerId = this.offer?.id ?? this.offer.inquiryVersion?.inquiry.id;
    if (!offerId) {
      console.warn('Offer id unavailable for task routing');
      return;
    }

    switch (id) {
      case 1:
        this.router.toTaskSearchWithBo(BusinessObjectType.BONDING_CONTRACT_OFFER, offerId);
        break;
      case 2:
        this.router.toNewTaskDetailsWithBO(BusinessObjectType.BONDING_CONTRACT_OFFER, offerId);
        break;
      case 3:
        this.createPrepareSuretyProposalTask();
        break;
      default:
        console.log('Action not defined');
    }
  }

  get isAutomaticGroupSelection(): boolean {
    // "Automatic group selection" selcted as voting as votingProcessType
    return this.offer?.voting != null && this.offer.voting.votingProcessType == null;
  }

  onVotingProcessTypeChange(): void {
    this.loadAssigners();
  }

  // Enables "Yes" only when required assigners are chosen
  validVotingInput(): boolean {
    if (this.votingSimulationFailed) {
      return false; // voting simulation failed can't proceed
    }
    if (!this.isAutomaticGroupSelection) {
      return true;
    }
    const level_2_assigner = !this.showLevel2Assigner || !!this.selectedLevel2Assigner;
    const level_3_assigner = !this.showLevel3Assigner || !!this.selectedLevel3Assigner;
    const levelSuretyTeamLeadersOk = !this.showSuretyTeamLeaders || !!this.selectedSuretyTeamLeaders;
    return level_2_assigner && level_3_assigner && levelSuretyTeamLeadersOk;
  }

  loadAssigners(): void {
    this.votingSimulationFailed = false;
    if (!this.isAutomaticGroupSelection) {
      this.showLevel2Assigner = false;
      this.showLevel3Assigner = false;
      this.showSuretyTeamLeaders = false;
      return;
    }
    this.offerService.simulateVoting(this.offer.id).subscribe({
      next: (res) => this.applyAssigners(res),
      error: (error) => {
        this.votingSimulationFailed = true; // confirm Yes button disabled
        this.handleServerError(error, true, false);
      },
    });
  }

  private applyAssigners(res: VotingProcessDto): void {
    this.offer.voting.authorityLevel = res.authorityLevel;
    this.level2Assigner = res.level2Assigners || [];
    this.level3Assigner = res.level3Assigners || [];
    this.levelSuretyTeamLeader = res.suretyTeamLeaders || [];
    this.showLevel2Assigner = (res.level2Assigners || []).length > 0;
    this.showLevel3Assigner = (res.level3Assigners || []).length > 0;
    this.showSuretyTeamLeaders = (res.suretyTeamLeaders || []).length > 0;
    this.selectedLevel2Assigner = null!;
    this.selectedLevel3Assigner = null!;
    this.selectedSuretyTeamLeaders = null!;
  }
}

export class BondingContractOfferViewParams {
  // string when reading
  readFromCache?: string | boolean;
  inquiryVersionId?: string | number;
  edgeOfferId?: string | number;
  copyOfferId?: string | number;
}
